/**
 * Shared (client- and server-safe) helpers for the demo-request flow:
 * deriving a company from the work email, flagging personal-mailbox
 * domains, and humanizing country codes for notifications.
 */

// Consumer/free mailbox providers — an email on one of these tells us nothing
// about the visitor's company, so we skip company derivation for them.
const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.fr',
  'hotmail.com',
  'hotmail.co.uk',
  'hotmail.fr',
  'outlook.com',
  'outlook.sa',
  'live.com',
  'live.co.uk',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'gmx.com',
  'gmx.de',
  'gmx.net',
  'mail.com',
  'mail.ru',
  'yandex.com',
  'yandex.ru',
  'zoho.com',
  'fastmail.com',
  'hey.com',
  'qq.com',
  '163.com',
  '126.com',
  'web.de',
  't-online.de',
  'orange.fr',
  'wanadoo.fr',
  'free.fr',
  'naver.com',
  'daum.net',
  'rediffmail.com',
]);

// Multi-part public suffixes we're likely to meet, so "acme.co.uk" derives
// "Acme", not "Co". Not exhaustive on purpose — unknown suffixes just fall
// back to second-level label, which is right for ~every business domain.
const TWO_LEVEL_TLDS = new Set([
  'co.uk',
  'org.uk',
  'ac.uk',
  'com.au',
  'com.br',
  'com.mx',
  'com.sa',
  'com.eg',
  'com.tr',
  'co.jp',
  'co.in',
  'co.za',
  'co.nz',
  'com.sg',
  'com.hk',
  'com.my',
  'com.qa',
  'com.kw',
  'com.bh',
  'com.jo',
  'com.lb',
]);

export function emailDomain(email: string): string | null {
  const at = email.lastIndexOf('@');
  if (at === -1) return null;
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  return domain.includes('.') ? domain : null;
}

export function isFreeEmailDomain(domain: string): boolean {
  return FREE_EMAIL_DOMAINS.has(domain);
}

/**
 * "jane@acme-labs.io" → "Acme Labs". Free-mail and unparseable domains → null;
 * the caller simply omits company data rather than sending junk to the CRM.
 */
export function deriveCompanyFromEmail(email: string): {
  domain: string;
  companyName: string;
} | null {
  const domain = emailDomain(email);
  if (!domain || isFreeEmailDomain(domain)) return null;

  const parts = domain.split('.');
  let label: string | undefined;
  if (parts.length >= 3 && TWO_LEVEL_TLDS.has(parts.slice(-2).join('.'))) {
    label = parts[parts.length - 3];
  } else if (parts.length >= 2) {
    label = parts[parts.length - 2];
  }
  if (!label) return null;

  const companyName = label
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  return companyName ? { domain, companyName } : null;
}

/** "SA" → 🇸🇦 via regional-indicator math; null for anything not two A–Z. */
export function countryFlagEmoji(countryCode: string | null): string | null {
  if (!countryCode || !/^[A-Za-z]{2}$/.test(countryCode)) return null;
  const base = 0x1f1e6; // regional indicator A
  const code = countryCode.toUpperCase();
  return String.fromCodePoint(
    base + code.charCodeAt(0) - 65,
    base + code.charCodeAt(1) - 65,
  );
}

const REGION_NAMES =
  typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

export function countryName(countryCode: string | null): string | null {
  if (!countryCode || !/^[A-Za-z]{2}$/.test(countryCode)) return null;
  try {
    return REGION_NAMES?.of(countryCode.toUpperCase()) ?? null;
  } catch {
    return null;
  }
}
